/**
 * Pure sizing and formatting for the in-flight row — the single line that sits
 * under the transcript while a turn is in flight:
 *
 *     14s · 1.2k tokens · Thinking                   working up to an answer
 *     23s · 1.9k tokens                              answer streaming
 *     14s · 1.2k tokens · Thinking · 2 running subtasks  sub-agents out
 *
 * Split out of ChatView so the arithmetic that carries the measurement claims
 * below is testable without rendering a chat: the divisors are the one part of
 * this feature that can silently drift into being wrong, and nothing about
 * them needs a DOM.
 */

/**
 * Characters of reasoning text per output token.
 *
 * Measured against ground truth from openclaw's own `model.completed` usage
 * records, on reasoning text specifically — three runs, same model:
 *
 *     3.31, 3.30, 2.50   (mean ~3.0)
 *
 * Reasoning text tokenises far more densely than the 4.3-5.0 ordinary prose
 * measures, which is why this is its own constant rather than one shared with
 * anything sizing visible output.
 *
 * 3.0 centres that spread. Do NOT read the tight agreement of the first two
 * samples as precision: the third landed 25% away, and picking a constant from
 * the first two produced a systematic undercount.
 */
export const THINKING_CHARS_PER_TOKEN = 3.0;

/**
 * Characters of ANSWER text per output token — a separate constant because
 * visible prose tokenises far more loosely than reasoning does.
 *
 * Measured the same way, against `model.completed` usage records: 5.02, 4.32,
 * 4.89 and 5.13 on prose-heavy turns, 3.09 on a dense numeric one. 4.5 centres
 * that. Reasoning sits near 3.0, so applying one divisor to both would bias
 * whichever phase it was not tuned for.
 */
export const RESPONSE_CHARS_PER_TOKEN = 4.5;

/**
 * Streamed characters (reasoning + answer) before a count is worth showing.
 *
 * Guards the very start of a run, where a couple of frames have landed and
 * each new one swings the estimate by a large fraction — and where rendering
 * "1 tokens" says nothing. Not a quality gate on the estimate itself.
 *
 * Kept low on purpose. At 200 this needed roughly two seconds of reasoning to
 * appear, so any turn that thought briefly showed a bare clock and the count
 * looked broken; only long reasoning runs ever revealed it. 80 characters is
 * about 27 tokens, which shows up within a few hundred milliseconds while
 * still skipping the single-digit noise.
 */
export const MIN_CHARS_TO_ESTIMATE = 80;

/** Separator between the in-flight row's fields. */
export const STATUS_DELIMITER = ' · ';

/**
 * Sizes a live token estimate from the text streamed so far, or returns null
 * while there is too little to be worth showing.
 *
 * No exact count is available while a run is in flight: the gateway never
 * pushes usage, and the provider itself only reports it in the final chunk of
 * the stream. What IS available is the text being streamed, so the figure is
 * sized from how much has arrived, at a different ratio per phase.
 *
 * The count spans the WHOLE turn: reasoning first, then the answer. Dropping
 * it when the answer started left the number frozen for what is usually the
 * longer half of the run — measured at 9 of 11 seconds on one turn — which
 * read as the counter having died.
 *
 * Measured end to end against openclaw's `model.completed` records — five
 * varied turns, final displayed value vs actual output tokens:
 *
 *     +5.1%  +2.5%  +27.5%  +9.6%  -26.0%
 *     mean absolute 14.2%, mean signed +3.7%
 *
 * The near-zero signed mean says the divisors are centred; the spread is
 * content sensitivity, not bias. The two worst cases show where it breaks:
 * very short turns, where 22 tokens of absolute error reads as 27% relative,
 * and dense algebra, which tokenises nearer 3.5 chars/token than the 4.5 the
 * response constant assumes. Long prose-heavy turns land inside 5%.
 *
 * That is well inside what a progress indicator needs, which is why the number
 * is rendered plainly rather than hedged with a "~": anything needing an exact
 * count has to wait for end-of-turn usage, which this is not, and a tilde
 * would buy precision theatre at the cost of a scannable row.
 *
 * Do not chase this with a real tokeniser. It would be model-specific (this
 * gateway fronts several), cost a dependency and per-frame CPU, and buy
 * precision a progress indicator does not need.
 */
export function estimateTokens(
  thinkingChars: number,
  responseChars: number,
): number | null {
  if (thinkingChars + responseChars < MIN_CHARS_TO_ESTIMATE) return null;
  return Math.round(
    thinkingChars / THINKING_CHARS_PER_TOKEN +
      responseChars / RESPONSE_CHARS_PER_TOKEN,
  );
}

/**
 * Compact token rendering: exact under 1k, then 1.2k / 24k / 1.2M. Precision
 * drops as magnitude climbs because the trailing digit stops carrying meaning.
 *
 * The thresholds are the ROUNDING boundaries, not the magnitude boundaries,
 * and the difference is visible: at one decimal 9,999 renders as "10.0k",
 * which is a five-character way of writing "10k", and at no decimals 999,500
 * renders as "1000k" rather than "1.0M". Each branch therefore ends where its
 * own rounding would spill into the next unit.
 */
export function formatTokens(total: number): string {
  if (total < 1000) return String(total);
  if (total < 9_950) return `${(total / 1000).toFixed(1)}k`;
  if (total < 999_500) return `${(total / 1000).toFixed(0)}k`;
  return `${(total / 1_000_000).toFixed(1)}M`;
}

/**
 * Names the sub-agent subtasks running under this chat, or null when none are.
 *
 * "Subtask" because the sidebar's Tasks pane is scheduled cron jobs, and this
 * is not that. A count, not titles: what each child is doing lives in its own
 * session, which this UI has no view onto.
 */
export function formatRunningSubtasks(count: number): string | null {
  if (count <= 0) return null;
  return count === 1 ? '1 running subtask' : `${count} running subtasks`;
}

/**
 * Formats a run's age the way a status line should read: seconds while the
 * number stays small, minutes once "97s" stops being scannable at a glance.
 */
export function formatElapsed(totalSeconds: number): string {
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds}s`;
}

/**
 * Builds the row's text: elapsed time, then the estimate once there is enough
 * text to size one, then the gateway's own state label.
 *
 * Fixed order, least to most volatile: the clock always moves, the count moves
 * while text streams, the status changes rarely. Anything absent drops out
 * rather than rendering a placeholder, so the row never shows an empty slot or
 * a stray delimiter.
 *
 * The label is dropped once the answer is streaming: the text on screen says
 * what a "Writing" label would, and the row keeps to the two things the text
 * cannot tell you — how long, and how much.
 *
 * The subtask count survives that drop: a sub-agent keeps working while the
 * parent writes, and the streaming text says nothing about it.
 */
export function buildStatusText({
  elapsedSeconds,
  thinkingChars,
  responseChars,
  label,
  isResponding,
  runningSubtasks = 0,
}: {
  elapsedSeconds: number;
  thinkingChars: number;
  responseChars: number;
  label: string;
  isResponding: boolean;
  runningSubtasks?: number;
}): string {
  const parts = [formatElapsed(elapsedSeconds)];
  const estimated = estimateTokens(thinkingChars, responseChars);
  if (estimated != null) parts.push(`${formatTokens(estimated)} tokens`);
  if (!isResponding && label.length > 0) parts.push(label);
  const subtasks = formatRunningSubtasks(runningSubtasks);
  if (subtasks != null) parts.push(subtasks);
  return parts.join(STATUS_DELIMITER);
}

/**
 * The still-streaming message belonging to `runId`, or — when the run is not
 * yet named — the newest still-streaming message.
 *
 * Run-keyed rather than positional, because "the streaming message" is not
 * unique: a cron turn can stream alongside an interactive one, and sizing the
 * row's count off whichever happened to come first would report the other
 * run's answer. When the indicator names a run, ONLY that run's bubble counts;
 * finding none means the answer has not started, which is different from
 * finding someone else's.
 *
 * The fallback covers the window before any frame has carried a runId (the row
 * is raised optimistically at send time) and gateways that omit runIds
 * entirely. It searches from the END so a stale bubble left open by a dropped
 * end-of-run frame cannot shadow the live one and freeze the count — measured:
 * a later run opening at the stale 1,383-character length and sitting there
 * for its whole duration.
 *
 * Hand-rolled rather than `Array.prototype.findLast` — that is ES2023 and this
 * workspace compiles against the ES2022 lib.
 */
export function findStreamingMessage<
  T extends { readonly isStreaming?: boolean; readonly runId?: string },
>(messages: readonly T[], runId?: string): T | undefined {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message?.isStreaming !== true) continue;
    if (runId == null || message.runId === runId) return message;
  }
  return undefined;
}
